import { Stack } from "@mui/material";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useUpdateIntakeMutation } from "src/api/services/intakesApi";
import { useGetQuestionsQuery } from "src/api/services/questionsApi";
import { InputType, Intake, Question, UpdateIntake } from "src/api/types";
import Input from "src/components/FormInput";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import DrawerForm from "src/shared/DrawerForm";

interface Props {
  open: boolean;
  onClose: () => void;
  intake: Intake;
}

const useInputs = () => {
  const [values, setValues] = useState<UpdateIntake>({
    first_question: "",
  });

  const setFields = useCallback(
    <K extends keyof UpdateIntake>(key: K, value: UpdateIntake[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );
  const { questions } = useGetQuestionsQuery(
    { from: 0, to: 1000 },
    {
      selectFromResult: ({ data }) => {
        return { questions: data?.data ?? [] };
      },
    },
  );

  const inputs: InputType<Question>[] = useMemo(
    () => [
      {
        type: "autocomplete",
        placeholder: "First question",
        options: questions,
        value: questions.find((q) => q.slug === values.first_question) ?? null,
        onChange: (v) => setFields("first_question", v?.slug ?? ""),
        required: false,
        label: "First Question",
        getOptionLabel: (q) => `${q.id}: ${q.title}`,
        getOptionDescription: (q) => q.subtitle ?? undefined,
      },
    ],
    [questions, setFields, values.first_question],
  );

  return { values, inputs, setValues };
};

const UpdateIntakeDrawer = ({ intake, onClose, open }: Props) => {
  const { inputs, values, setValues } = useInputs();
  const [loading, setLoading] = useState<boolean>(false);
  const [updateIntake] = useUpdateIntakeMutation();
  const errLog = useSentryLogger("error");

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      try {
        await updateIntake({
          id: intake.id,
          product_slug: intake.product.slug,
          body: { ...values },
        });

        setValues({
          first_question: "",
        });
        onClose();
      } catch (err) {
        errLog(`Unable to update intake: ${intake.id}`, err);
      } finally {
        setLoading(false);
      }
    },
    [
      errLog,
      intake.id,
      intake.product.slug,
      onClose,
      setValues,
      updateIntake,
      values,
    ],
  );

  useEffect(() => {
    setValues({
      first_question: intake.first_question.slug,
    });
  }, [intake.first_question.slug, intake.first_question.title, setValues]);

  return (
    <DrawerForm
      label="Update Intake"
      onClose={onClose}
      open={open}
      handleSubmit={handleSubmit}
      loading={loading}
    >
      <Stack gap={2} component={"form"}>
        <Stack gap={2}>
          {inputs.map((i) => (
            <Input key={i.label} current={i} />
          ))}
        </Stack>
      </Stack>
    </DrawerForm>
  );
};

export default UpdateIntakeDrawer;
